/**
 * The Help centre's guides, as data.
 *
 * Every sentence here was written from the code that does the thing, not from
 * a description of it: the iPhone app's Swift under `apps/ios/Sources`, the
 * portal's screens beside this file, the shared rules in `packages/`, and the
 * sourced figures in `docs/research/tape-measure-2026-09-10.md`. Where the app
 * shows a label, the guide uses the same words, in **bold**, so a reader can
 * find the control by what it says. When the app changes, the guide that
 * describes it must change in the same commit.
 *
 * Three kinds of note carry what the body text must not blur:
 *   - `device`  — only checkable on a real iPhone (LiDAR, camera, haptics,
 *                 iOS permissions);
 *   - `general` — ordinary scanning advice, not a Scannix feature;
 *   - `caution` — a limit of the current build, or of the sensor.
 *
 * Structured rather than written as pages so one component renders every
 * guide the same way, and so search can read the same words the reader sees.
 * Inline `**…**` marks a label as it appears on screen; nothing else is markup.
 */

export const HELP_GUIDE_IDS = [
  "before-you-scan",
  "scanning-a-room",
  "floors-and-stairs",
  "terraced-and-tight-rooms",
  "3d-space",
  "tape-measure",
  "sending-a-survey",
  "laser-calibration",
  "floor-plan",
  "exports-and-reports",
  "sharing-a-survey",
  "accuracy",
] as const;

export type HelpGuideId = (typeof HELP_GUIDE_IDS)[number];

/** Where the thing a guide describes is done. */
export type HelpSurface = "iphone" | "portal";

export const SURFACE_LABEL: Readonly<Record<HelpSurface, string>> = {
  iphone: "iPhone app",
  portal: "Portal",
};

export type HelpNoteTone = "device" | "general" | "caution";

export const NOTE_LABEL: Readonly<Record<HelpNoteTone, string>> = {
  device: "Needs a real iPhone",
  general: "General advice, not a Scannix feature",
  caution: "Worth knowing",
};

export type HelpBlock =
  | { readonly kind: "p"; readonly text: string }
  | { readonly kind: "steps"; readonly items: readonly string[] }
  | { readonly kind: "list"; readonly items: readonly string[] }
  | { readonly kind: "note"; readonly tone: HelpNoteTone; readonly text: string };

export interface HelpSection {
  readonly heading: string;
  readonly blocks: readonly HelpBlock[];
}

export type HelpTopic = "on-site" | "portal" | "background";

export const HELP_TOPICS: readonly { readonly id: HelpTopic; readonly title: string }[] = [
  { id: "on-site", title: "On site, with the iPhone" },
  { id: "portal", title: "In the portal" },
  { id: "background", title: "Good to know" },
];

export interface HelpGuide {
  readonly id: HelpGuideId;
  readonly title: string;
  /** One or two sentences: what the guide answers. Shown in the list. */
  readonly summary: string;
  readonly appliesTo: readonly HelpSurface[];
  readonly topic: HelpTopic;
  readonly sections: readonly HelpSection[];
  readonly related: readonly HelpGuideId[];
  /** Words a reader might search for that the text does not use. */
  readonly keywords?: readonly string[];
}

const p = (text: string): HelpBlock => ({ kind: "p", text });
const steps = (...items: string[]): HelpBlock => ({ kind: "steps", items });
const list = (...items: string[]): HelpBlock => ({ kind: "list", items });
const note = (tone: HelpNoteTone, text: string): HelpBlock => ({ kind: "note", tone, text });

export const HELP_GUIDES: readonly HelpGuide[] = [
  // -------------------------------------------------------------------- 1 --
  {
    id: "before-you-scan",
    title: "Before you scan",
    summary:
      "The quick checklist the app shows before the first room of a visit, and why each item is on it.",
    appliesTo: ["iphone"],
    topic: "on-site",
    keywords: ["checklist", "battery", "storage", "low power", "preparation", "prepare"],
    sections: [
      {
        heading: "When it appears",
        blocks: [
          p(
            "Tap **Capture**, then **Scanner**, and name the property (or tap **Skip**). The checklist, headed **Before you scan**, appears before the first room of the visit. It does not appear again when you tap **Scan another room**.",
          ),
          p(
            "Ticking the items is for your own benefit. **Start scanning** works whether or not anything is ticked.",
          ),
        ],
      },
      {
        heading: "The room",
        blocks: [
          list(
            "**Open every door you'll walk through.** A door that moves during the scan confuses it. Open them first and leave them open.",
            "**Turn all the lights on.** The scanner tracks far better in a bright room.",
            "**Clear the floor edges and corners.** Bags and boxes against a wall hide the corner, and the corners are where the walls are measured from.",
            "**Cover or avoid big mirrors and glass.** The sensor sees a mirror as another room behind the wall.",
            "**More than one floor?** Start on the lowest floor, and walk the stairs slowly to join the levels.",
          ),
          p("Under the list: walk slowly, hold the phone at chest height, and sweep every corner."),
        ],
      },
      {
        heading: "This phone",
        blocks: [
          p("The app reads three things for itself and shows each as a tick or a warning:"),
          list(
            "**Battery 80% or more.** A scan runs the LiDAR sensor, the camera and the 3D reconstruction at once. It drains the battery fast and can make the phone too hot to scan, and a whole-house walk can take 30 to 60 minutes.",
            "**Low Power Mode off.** It slows the phone down, which can make the scan stutter. Turn it off in Settings › Battery.",
            "**2 GB free or more.** Every photo is a full-size stamped picture, saved to the survey and (with the gallery setting on) to Photos as well. A 150-photo survey comes to about 1 GB.",
          ),
          p(
            "If the phone cannot read a value, it shows a question mark instead of a verdict. That never stops you; check it yourself.",
          ),
        ],
      },
      {
        heading: "Don't show the tips again",
        blocks: [
          p(
            "The switch at the bottom hides the room tips on this phone only. A failing battery, Low Power Mode or storage check still appears on its own, headed **Check this first**, because those are things you cannot already know.",
          ),
          note(
            "device",
            "The checklist only appears on a phone that can scan: an iPhone Pro with a LiDAR sensor. On other iPhones the **Scanner** card says **Needs an iPhone Pro with a LiDAR sensor**.",
          ),
        ],
      },
    ],
    related: ["scanning-a-room", "floors-and-stairs", "terraced-and-tight-rooms"],
  },

  // -------------------------------------------------------------------- 2 --
  {
    id: "scanning-a-room",
    title: "Scanning a room",
    summary:
      "One room at a time: the walk, the live coaching banners, taking photos while you scan, and what to press when a room is done.",
    appliesTo: ["iphone"],
    topic: "on-site",
    keywords: ["roomplan", "lidar", "walk", "banner", "coaching", "shutter", "photo", "stop", "finish walk", "merge"],
    sections: [
      {
        heading: "The walk",
        blocks: [
          steps(
            "Tap **Capture**, then **Scanner**. Type the address or a reference (or **Skip** and name it later), then **Start scan**.",
            "Scanning starts straight away. Begin in a corner.",
            "Walk the room slowly, phone at chest height, covering every wall. Watch the model build at the bottom of the screen.",
            "Tap **Stop** when every wall is in.",
          ),
        ],
      },
      {
        heading: "The coaching banners",
        blocks: [
          p(
            "One banner shows at a time, across the top of the screen, and the phone gives a buzz you can feel without looking:",
          ),
          list(
            "**Too dark**: turn on a light, or use the torch. This is the only banner with a **Torch** switch.",
            "**Slow down**: move and turn the phone more slowly.",
            "**Step back from the wall**: you're too close for the sensor to see it.",
            "**Move closer to the wall**: walk nearer so the sensor can reach it.",
            "**Not enough detail here**: aim at an edge, such as a door frame, skirting or furniture.",
          ),
          p(
            "If two things are wrong at once, the most urgent shows: darkness first, then speed. A banner stays up for a couple of seconds so it can be read, and the same warning buzzes at most once every few seconds so it does not nag. A small chip shows how many walls have been found so far.",
          ),
          p(
            "Once three walls are found and about 20 seconds have passed (or after 45 seconds in any case), a reminder appears: sweep each corner and the line where the walls meet the floor. That is where a quick pass most often leaves a wall short.",
          ),
        ],
      },
      {
        heading: "Taking photos while you scan",
        blocks: [
          p(
            "The round white camera button on the right takes a photo from the scanning camera. The screen flashes, the phone buzzes and plays the shutter sound (silent when the phone is on silent). It takes at most one photo a second, so a fumbled double-tap does not make duplicates.",
          ),
          p(
            "Photos are not taken automatically during a scan. The automatic photo every two seconds was switched off because in small rooms it filled the survey with pictures of the wall an inch from the lens.",
          ),
          p(
            "Scan photos are stamped with the property name, the date and the time, and are copied to your Photos when the gallery setting is on. For photos that also carry a GPS position and compass bearing, use **Take photos**.",
          ),
        ],
      },
      {
        heading: "Stopping: the pre-stop check",
        blocks: [
          p(
            "When you tap **Stop**, the app checks what the scanner has. If it has found fewer than four walls, or is unsure of some surfaces, a card asks you to **Keep scanning** or **Stop anyway**.",
          ),
          p(
            "Keeping going costs seconds. A stopped room cannot be resumed, only scanned again from the start. An open-plan room can really have three walls, so this is a question, never a block.",
          ),
        ],
      },
      {
        heading: "When the room is captured",
        blocks: [
          p(
            "The screen says **Room captured** with its count of walls, windows and doors. The room is saved on the phone at that moment, so closing the screen now does not lose it.",
          ),
          list(
            "If some surfaces came in with low confidence, a card lists them (for example **Wall 3 of 5**) with **Re-scan this room** or **Save it anyway**. Re-scanning asks you to confirm; the photos you took are kept.",
            "**Scan another room** is the big button: it starts the next room straight away.",
            "**Finish walk — merge into a structure** sits below **Done with this property?**. It merges every room into one model and shows it, with a list of rooms per storey, **Re-scan a storey** and **Done**. The merge can take from a few seconds to tens of seconds on a many-room walk.",
          ),
        ],
      },
      {
        heading: "If a room cannot be added",
        blocks: [
          p(
            "**Scan couldn't be added** most often means the phone lost track of where it was while walking through a doorway, so the new room will not line up with the ones already scanned. Tap **Try scanning again**, starting from a corner of the room. If it keeps failing, **Start walk over (discard previous rooms)** makes this room the first of a fresh walk.",
          ),
          p("If tracking was lost, move a little more slowly and keep some wall or floor in view."),
          note(
            "device",
            "Scanning, the banners, the buzzes and the torch only work on an iPhone Pro with a LiDAR sensor. None of it can be tried in the simulator.",
          ),
        ],
      },
    ],
    related: ["before-you-scan", "floors-and-stairs", "sending-a-survey"],
  },

  // -------------------------------------------------------------------- 3 --
  {
    id: "floors-and-stairs",
    title: "More than one floor, and stairs",
    summary:
      "How rooms are tagged with a storey, walking between floors, checking the merged model before you leave, and a limit of this build.",
    appliesTo: ["iphone", "portal"],
    topic: "on-site",
    keywords: ["storey", "story", "level", "upstairs", "landing", "basement", "multi-storey"],
    sections: [
      {
        heading: "Walking between floors",
        blocks: [
          p(
            "Start on the lowest floor. When you go up, walk the stairs slowly with the phone up so the levels join, the same advice the pre-scan checklist gives.",
          ),
        ],
      },
      {
        heading: "Storeys",
        blocks: [
          p(
            "Every room carries a storey: **Ground floor**, **First floor** to **Fourth floor**, or **Basement** and **Basement 2** below ground — the same names the portal, the plan exports and the installer pack print. When you tap **Finish walk**, the floors are stacked by that storey. How far apart the floors sit comes from the storey order, not from a LiDAR measurement, so an upper floor can come out slightly offset or turned. Each room's own sizes are not affected.",
          ),
          note(
            "caution",
            "A limit of this build: the storey is chosen with the **Storey** stepper on the **Start scanning this room** screen, but a new walk starts scanning straight away on **Ground floor**, and **Scan another room** keeps the same storey. So the stepper is not offered between rooms, and every room of a walk is tagged Ground floor. The stepper only appears after **Finish walk** › **Re-scan a storey**. Until this changes, expect upstairs rooms to be drawn on the Ground floor sheet in the portal.",
          ),
        ],
      },
      {
        heading: "Check the merged model before you leave",
        blocks: [
          p(
            "After **Finish walk** the phone lists the rooms on each storey and shows the merged 3D model. Look for a floor that sits outside the one below it, is turned, or floats.",
          ),
          steps(
            "Tap **Re-scan a storey** and pick the floor that is wrong. Its rooms are thrown away; other storeys are kept.",
            "You land on **Start scanning this room** with that storey already set. Walk it again.",
          ),
          p("Fixing it on site costs minutes. Once you have left, it costs a return visit."),
        ],
      },
      {
        heading: "In the portal",
        blocks: [
          list(
            "When a scan has more than one storey, the plan's toolbar has a **Storey** picker.",
            "In **Export**, a **Floor** picker chooses the storey for the PNG, SVG and DXF. The PDF prints every floor anyway, a sheet each.",
            "Pipe drawn on the plan is measured along the runs on each floor. Rises and drops between floors cannot be on a plan, so add them. The stock report has allowances for drops per radiator and for extra pipe such as risers.",
          ),
        ],
      },
    ],
    related: ["scanning-a-room", "floor-plan", "exports-and-reports"],
  },

  // -------------------------------------------------------------------- 4 --
  {
    id: "terraced-and-tight-rooms",
    title: "Terraced houses and tight rooms",
    summary:
      "Small rooms, WCs, cupboards, narrow halls and party walls. Some of this is general scanning advice rather than a Scannix feature, and it says which.",
    appliesTo: ["iphone", "portal"],
    topic: "on-site",
    keywords: ["terrace", "small room", "wc", "toilet", "cupboard", "hallway", "party wall", "narrow", "zoom", "wide"],
    sections: [
      {
        heading: "What the app does in a small room",
        blocks: [
          p(
            "The scanner uses the phone's standard (1×) camera, and the app cannot zoom it out. In a small room you will not fit a whole wall in view at once. That is normal: the sensor builds each surface up over several passes. If a surface is still uncertain when you tap **Stop**, the pre-stop check says so and offers **Keep scanning**.",
          ),
          p(
            "The **Step back from the wall** banner means you are too close for the sensor to see the wall.",
          ),
          p(
            "Photos taken during a scan come from the same 1× camera. For a wider photo of a small room, use **Take photos**, which offers 0.5× on iPhones with an ultra-wide lens.",
          ),
        ],
      },
      {
        heading: "Practical tips",
        blocks: [
          note(
            "general",
            "These are ordinary habits for scanning tight spaces. The app does not check any of them.",
          ),
          list(
            "Start from the doorway when you can, so the first view takes in as much of the room as possible.",
            "In a WC or cupboard, sweep the phone slowly round the edges from where you stand rather than squeezing round the room.",
            "Clear bags and boxes away from the walls. A box against a wall hides the corner.",
            "Walk through doorways slowly. A doorway is where tracking most often drifts between rooms.",
            "Treat a narrow hall or landing as a room of its own.",
          ),
        ],
      },
      {
        heading: "Party walls",
        blocks: [
          p(
            "Scannix can only tell a wall is internal when a scanned room is on the other side of it. A wall with nothing scanned behind it, which on a terrace includes the party walls, is treated as an outside wall when the scan is turned into a heat loss.",
          ),
          note(
            "caution",
            "A wall to the house next door loses far less heat than an outside wall. Before relying on the heat loss of a terraced or semi-detached house, check how its party walls have been treated.",
          ),
          p(
            "On the Survey page, under **Rooms** and in the **Walls** table under the 3D view, each of those walls is marked **Outside wall? — may be a party wall**. Pick the wall in the Walls table to see which one it is in 3D or on the plan. Choose **Party wall (adjoining home)**, **Unheated space (garage, store…)** or **Confirm outside wall**. A party wall or an unheated space needs the temperature on the other side, from your design guide: MCS doesn't set one. The heat loss is recalculated and the wall shows who confirmed it. A laser reading or a correction on the plan afterwards changes the wall's size but keeps your answer and its temperature. If you re-scan the house, a room with any wall you have answered (party wall, unheated space or confirmed outside) is kept as it is, so you are not asked again.",
          ),
        ],
      },
      {
        heading: "Mirrors and glass",
        blocks: [
          p(
            "Cover or avoid big mirrors and glass. The sensor sees a mirror as another room behind the wall, and plate glass and mirrored walls are cases Apple itself names as hard.",
          ),
        ],
      },
    ],
    related: ["before-you-scan", "scanning-a-room", "accuracy"],
  },

  // -------------------------------------------------------------------- 5 --
  {
    id: "3d-space",
    title: "3D Space (Space Mode)",
    summary:
      "A detailed, textured 3D model of a room: the guide, the walking pattern, 40 to 120 frames, sending it, and what the portal shows while it is built.",
    appliesTo: ["iphone", "portal"],
    topic: "on-site",
    keywords: ["space mode", "textured", "photogrammetry", "model", "usdz", "frames", "mesh", "3d model"],
    sections: [
      {
        heading: "Which mode",
        blocks: [
          p(
            "**Capture** › **3D space** opens **Creating a 3D Space**, with a choice of two modes:",
          ),
          list(
            "**Floorplan Mode** is the same LiDAR room scan as the **Scanner** card (iPhone Pro only). It gives the clean model the plan and every measurement come from.",
            "**Space Mode** walks the room with the camera, and a server builds a detailed, textured model from the pictures. The guide suggests Space Mode when in doubt.",
          ),
          p("Tap **Capture a space** to start."),
        ],
      },
      {
        heading: "The walk",
        blocks: [
          steps(
            "Prepare: lights on, doors open, floor clear. Plan one loop around the room.",
            "Walk slowly around the edge of the room, phone upright, pointing at the walls and corners so each view overlaps the last.",
            "Keep going until the coverage ring is lit all round, then tap **Done**.",
          ),
          p(
            "Frames are taken for you: one each time the phone has moved about 20 cm or turned about 12°, as long as it is tracking, not turning so fast the picture smears, and the room is bright enough. The big white button takes one now, for a corner the automatic capture skipped; hold still while it does.",
          ),
          p(
            "The banners say what is wrong: **Hold still while tracking starts**, **Move slowly**, **Point at something with more detail**, **Too dark — turn the lights on**.",
          ),
        ],
      },
      {
        heading: "How many frames",
        blocks: [
          list(
            "The count at the top coaches you: **Aim for 40–120**, then **Good — finish the loop**.",
            "**Done** needs at least 20 frames; until then it counts down how many more.",
            "The maximum is 200 frames.",
            "The ring at the bottom left has 12 segments, one per direction. An unlit segment is a wall you have not faced yet.",
          ),
        ],
      },
      {
        heading: "Done, then Send",
        blocks: [
          p(
            "**Done** saves the frames on the phone and queues them. Nothing is uploaded yet. Send the survey from **Projects** (see Sending a survey): the model is built on the server and appears on the survey in the portal a few minutes later.",
          ),
          p(
            "**Start over** deletes the frames taken so far, and leaving without **Done** discards them. Both ask you first.",
          ),
        ],
      },
      {
        heading: "What the portal shows",
        blocks: [
          p("On the Survey page, the **3D Space** section shows where the model is:"),
          list(
            "**Queued**: the capture has arrived and is waiting its turn. Models are built one at a time.",
            "**Processing**: being built, with a progress bar and the stage. A room usually takes one to three minutes.",
            "**The textured model could not be built**, with the reason. The flat scan and every measurement are not affected. Capture the room again and send the survey; the new capture replaces the old one.",
            "Ready: the model in a viewer with **Orbit**, **Pan**, **Measure**, **Fit to view**, **Reset view**, coordinate axes, a copy-link button and **Download model** (a USDZ file that opens in Quick Look on an iPhone, iPad or Mac).",
          ),
          p(
            "While a model is queued or processing, the status updates on its own every few seconds. **Check again** asks straight away.",
          ),
        ],
      },
      {
        heading: "Is it to scale?",
        blocks: [
          p(
            "On a LiDAR iPhone the frames carry depth, so the model is to scale (the viewer says **Metric (LiDAR depth)**) and **Measure** works. Without LiDAR the model is **Not to scale** and **Measure** is switched off. In a test, a room 2.5 m high came back 1.16 m high without depth and 2.53 m with it.",
          ),
          note(
            "caution",
            "The textured model shows what the room looks like. Take the sizes a design depends on from the scan and the plan, not from this model.",
          ),
          note(
            "device",
            "Space Mode needs a real iPhone camera with motion tracking, and does not run in the simulator. Any recent iPhone can capture; only a LiDAR iPhone gives a model to scale.",
          ),
        ],
      },
    ],
    related: ["sending-a-survey", "scanning-a-room", "accuracy"],
  },

  // -------------------------------------------------------------------- 6 --
  {
    id: "tape-measure",
    title: "Tape measure",
    summary:
      "Point at a radiator, window or door for its width × height and its height off the floor, with an honest ± on every figure.",
    appliesTo: ["iphone", "portal"],
    topic: "on-site",
    keywords: ["measure", "radiator", "window", "door", "sill", "mounting height", "width", "height", "rectangle", "w x h", "accuracy band", "glass", "dark"],
    sections: [
      {
        heading: "Where",
        blocks: [
          p(
            "**Capture** › **Tape measure**. Saved measurements are on the survey: on the phone under **Projects** › the survey › **Tape measurements**, and in the portal on the Survey page under **Tape measurements** once the survey is sent.",
          ),
        ],
      },
      {
        heading: "Three ways to measure",
        blocks: [
          list(
            "**W × H**: aim the centre at the object. When an outline appears, tap **Use** to take its width × height. Wrong shape, or a white radiator on a white wall? Tap the hand and place the corners yourself: bottom-left, bottom-right, then top-left.",
            "**Line**: tap **+** at each point, then **Finish**. The reticle snaps to edges and corners where two surfaces meet, with a small buzz.",
            "**Area**: tap **+** at each corner, then **+** back on the first point to close the area.",
          ),
          p("**Undo** takes back the last point; **Clear** starts again."),
        ],
      },
      {
        heading: "Height off the floor",
        blocks: [
          p(
            "The app looks for the floor itself (**Floor found**), or aim at the floor and tap **Set floor** (**Floor set by you**). With the floor known, the reticle shows the height live, for example **↕ 870 mm above floor**, before you place anything.",
          ),
          list(
            "A radiator's underside is saved as its mounting height.",
            "A window's bottom edge is saved as its sill height.",
            "A door whose bottom edge sits more than 50 mm off the floor is flagged.",
          ),
          p(
            "If the floor was not found, the save sheet says so and there is no height. Tap **Set floor** while aiming at the floor, then measure again.",
          ),
        ],
      },
      {
        heading: "Accuracy bands",
        blocks: [
          list(
            "Up to 1 m from the phone: ±1 cm a point.",
            "1 to 3 m: ±2 cm.",
            "3 to 4.5 m: ±3 cm, and the reticle says **Over 3 m — move closer**.",
            "Beyond 4.5 m the point is not placed: **Too far — move within 4.5 m**.",
            "A width × height from a detected outline is never labelled better than ±2 cm, because the outline's edges come from the camera picture, not the depth sensor.",
          ),
          p(
            "Each point is the middle reading of the last eight frames rather than one frame. If the phone moved while a point was placed, the reading is flagged.",
          ),
        ],
      },
      {
        heading: "Glass, mirrors and dark surfaces",
        blocks: [
          list(
            "**LiDAR unsure — glass, mirror or dark surface?** means the sensor's own confidence was low at the target. Glass, mirrors, gloss and very dark surfaces read badly.",
            "Aiming at a window through the glass: if the depth behind the reticle is more than 5 cm behind the wall, the reading went through the glass. The size is then taken on the wall instead, and flagged.",
            "A detected shape that is out of square may not be the whole object, and is flagged.",
          ),
          p("Any of these marks the reading as poor, and the warning is saved with it."),
        ],
      },
      {
        heading: "Saving",
        blocks: [
          p(
            "Choose what it is (radiator, window, door, wall or other), the room and an optional note, and the units: mm, cm or m. The measurement is saved with a photo showing the lines and figures, stamped with the date, time and location. It goes to the portal with the survey's **Send**, where the Survey page lists each one with its ± band, any warnings and the photo.",
          ),
          note(
            "caution",
            "A LiDAR figure is approximate. Use a laser or tape where a design depends on the last centimetre. The bands come from published studies of flat, matte, well-lit targets, and have not yet been checked against a tape on our own phones.",
          ),
          note(
            "caution",
            "Not in this build: readings from a Bluetooth laser meter, dragging the corners of a detected outline, and copying a mounting height into a radiator on the plan. The designer carries that figure across by hand.",
          ),
          note(
            "device",
            "The tape measure needs an iPhone Pro with a LiDAR sensor.",
          ),
        ],
      },
    ],
    related: ["accuracy", "laser-calibration", "sending-a-survey"],
  },

  // -------------------------------------------------------------------- 7 --
  {
    id: "sending-a-survey",
    title: "Sending a survey to the portal",
    summary:
      "Nothing leaves the phone until you send it. How Send works, what Sent, Partly sent and Send failed mean, the Local Network fix, and the gallery setting.",
    appliesTo: ["iphone"],
    topic: "on-site",
    keywords: ["sync", "upload", "send", "retry", "local network", "wifi", "offline", "gallery", "photos", "camera roll", "failed", "partly sent"],
    sections: [
      {
        heading: "Nothing uploads on its own",
        blocks: [
          p(
            "Everything you capture is saved on the phone first, with or without signal, and queued to upload. Uploading starts only when you send. A survey that is not meant for the portal never leaves the phone.",
          ),
        ],
      },
      {
        heading: "Send one survey",
        blocks: [
          steps(
            "Open **Projects**.",
            "Tap **Send** on the survey's row, or swipe the row to the right and tap **Send**.",
          ),
          p(
            "Send takes that survey's own items only: the survey, its scan, its photos, its tape measurements and any 3D Space capture. Nothing of another survey goes with it, even one of the same property.",
          ),
          p(
            "The **Sync now** button at the top of **Projects** sends everything queued on the phone, for every survey.",
          ),
        ],
      },
      {
        heading: "What the badge means",
        blocks: [
          list(
            "**Sent**: every item is on the server.",
            "**Partly sent**: some items are on the server and the rest are waiting.",
            "**Not sent**: nothing has gone yet.",
            "**Send failed**: the last attempt failed. The button now says **Retry**.",
          ),
          p(
            "Under the name, a line says what is still outstanding, for example **Scan sent · 3 of 12 photos failed**, and in red, why.",
          ),
        ],
      },
      {
        heading: "When a send fails",
        blocks: [
          list(
            "**Server did not answer in time. If the address is a 192.168.x number, allow Scannix under Settings › Scannix › Local Network, then retry.** iOS does not refuse a request it has blocked on the local network; it simply never answers it. So a time-out on an office address usually means the Local Network permission is off, not that the Wi-Fi is down.",
            "**Nothing is listening at that address and port**: the wrong port, or the server is not running.",
            "**This phone lost its network during the send — retry on Wi-Fi.**",
            "**Sign in expired — sign in again.**",
            "**No sync server set — add one in Settings.**",
            "**The server refused a photograph as altered (hash mismatch)**: the photograph that arrived did not match the one taken.",
          ),
          note(
            "device",
            "The Local Network permission is an iOS setting on the phone. It can only be checked on the phone itself.",
          ),
        ],
      },
      {
        heading: "Photos in your iPhone gallery",
        blocks: [
          p(
            "**Settings** › **Save photos to iPhone gallery** is on unless someone turned it off. Every photo you take is then also saved to your Photos, with its date, time and position stamp. The survey keeps its own copy either way: turning the setting off never removes a photo from a survey or stops it uploading.",
          ),
          list(
            "The app only asks to add photos. It never reads your library, and the permission prompt appears at the first photo, not when the app opens.",
            "**Also save unstamped originals**, under **Photos & camera**, adds a copy without the stamp.",
          ),
        ],
      },
      {
        heading: "After it arrives",
        blocks: [
          p(
            "In the portal, open the job, then **Open** on the survey. The scan and photographs appear once they have uploaded; a 3D Space model follows a few minutes later.",
          ),
          note(
            "caution",
            "Deleting a survey from the phone before it has finished sending loses that work for good. The phone asks before it deletes.",
          ),
        ],
      },
    ],
    related: ["3d-space", "tape-measure", "sharing-a-survey"],
  },

  // -------------------------------------------------------------------- 8 --
  {
    id: "laser-calibration",
    title: "Calibrating with a laser reading (optional)",
    summary:
      "Only if you need that level of accuracy: one wall, one laser reading, and the whole scan is corrected. Otherwise, move on.",
    appliesTo: ["portal"],
    topic: "portal",
    keywords: ["laser", "disto", "calibrate", "calibration", "scale", "correct", "factor", "accuracy"],
    sections: [
      {
        heading: "Do you need it?",
        blocks: [
          p(
            "Probably not. An uncalibrated scan is fine: its figures say **LiDAR scan**, and nothing asks you to calibrate it. Calibrate only when a design depends on figures closer than the scan's few centimetres.",
          ),
        ],
      },
      {
        heading: "One wall, one reading",
        blocks: [
          steps(
            "Open the survey in the portal and pick the room in the rooms list beside the scan.",
            "Under the room's name, click **Calibrate with a laser reading**.",
            "Choose the wall. A wall you clicked on the plan, in 3D or in a table is already chosen; otherwise the room's longest wall is. A long wall makes the best reference.",
            "Type what the laser read, in mm or m.",
            "Check the preview (the factor, and every wall of the room before and after), then click **Apply calibration**.",
          ),
          p(
            "The whole scan is corrected: every length is multiplied by the factor, every area by the factor squared, every volume by the factor cubed. The scan as captured is kept.",
          ),
        ],
      },
      {
        heading: "More options",
        blocks: [
          p("Closed by default, and none of them needed:"),
          list(
            "**Only correct this room**: for a multi-room scan whose rooms drifted differently. A room's own calibration wins over the whole-scan one for that room.",
            "**Add a check reading on another wall**: the corrected scan must agree with it within 10 mm, or 0.5% on a wall over 2 m.",
            "**Laser meter**: its make and model, for the record.",
          ),
        ],
      },
      {
        heading: "When it says no",
        blocks: [
          list(
            "A reading 2% or more away from the scan is refused. That is not a scale error but one misplaced corner, a different span measured (to a door frame or skirting, into a bay), or a typo. At 5% or more it is almost certainly the wrong wall or the wrong units.",
            "A wall shorter than 1.5 m is too short to take a factor from.",
            "A check reading that disagrees: **Scan is uneven here — measure this wall or rescan**.",
          ),
        ],
      },
      {
        heading: "Afterwards",
        blocks: [
          p(
            "The room shows, for example, **calibrated ×1.0071 from a laser reading (J. Ryder, 11 Sep)**, the readings behind it, and a button to remove it.",
          ),
          p(
            "The heat loss uses the calibrated figures. Anything a person entered or confirmed is kept: a U-value, a party wall and its temperature, an area typed in. If a kept figure is now more than 5% away from the calibrated scan, the **Heat loss** section lists it so you can check which is right.",
          ),
          list(
            "Dimensions you drew across the plan are not rescaled; they stay as drawn.",
            "Calibrating needs permission to edit the survey. Everyone else sees the result, but not the button.",
          ),
        ],
      },
    ],
    related: ["accuracy", "floor-plan", "tape-measure"],
  },

  // -------------------------------------------------------------------- 9 --
  {
    id: "floor-plan",
    title: "The floor plan",
    summary:
      "Correcting walls, doors and windows; dimensions, angles and areas; placing the heat pump, radiators and pipes; fit warnings and the MCS design check.",
    appliesTo: ["portal"],
    topic: "portal",
    keywords: ["plan", "edit", "wall", "door", "window", "dimension", "angle", "area", "radiator", "heat pump", "pipe", "fitting", "fit", "design check", "mcs", "undo"],
    sections: [
      {
        heading: "Opening the plan",
        blocks: [
          p(
            "On the Survey page, switch the scan from **3D** to **Plan**. Zoom with **+**, **−** and **Fit** at the bottom right. **Wall sizes** shows or hides every wall's length. Anything you pick on the plan is picked in the 3D view and the tables too.",
          ),
        ],
      },
      {
        heading: "Correcting the scan: Edit plan",
        blocks: [
          p("Click **Edit plan** (it needs permission to edit the survey). Then:"),
          list(
            "Drag a wall to move it. Its corners stay joined; hold Alt to move it alone, Shift to move it in any direction.",
            "Select a wall to drag its ends, or type its exact **Length** (choose which end stays put: **End A**, **The middle** or **End B**), **Thickness** in mm and **Height**. **Straighten** turns it square to the house.",
            "Drag a door or window along its wall, or select it to type its **Width**, **Height**, **Sill** (**Off the floor** for a door) and **Centre from end A**.",
            "**Add wall**, **Door**, **Window** and **Opening**: pick the tool, then click where it goes. Enter places it; Esc puts the tool down.",
            "Select a floor to rename the room.",
            "**Delete** removes; **Undo** and **Redo** (Ctrl/Cmd+Z, Ctrl/Cmd+Shift+Z) step through changes. Deleted items are listed with **Restore**.",
          ),
          p(
            "Corrections are saved with the survey and seen by the whole team. The scan itself is kept exactly as captured: **Revert to scan** takes a correction away.",
          ),
          p(
            "The heat loss follows your corrections: walls, floors and volumes are recalculated from the corrected plan. Anything a person entered or confirmed is kept: a U-value, a party wall and its temperature, an area typed in. If a kept figure now disagrees with the plan by more than 5%, or a wall someone answered for has been deleted from the plan, the **Heat loss** section lists it so you can check it.",
          ),
        ],
      },
      {
        heading: "Measuring on the plan",
        blocks: [
          list(
            "**Add dimension**: click two points. Ends snap to corners, then to walls.",
            "**Angle**: three clicks: the end of the first leg, the corner, the end of the second leg.",
            "**Area**: click round it corner by corner, then click the first point, double-click or press Enter to close it. It gives the m².",
          ),
          p(
            "Dimensions are saved with the survey, so everyone on the team sees them on any device. Label or remove each one under **Your dimensions**.",
          ),
        ],
      },
      {
        heading: "Objects: heat pump, radiators, pipes, fittings",
        blocks: [
          list(
            "**Heat pump**: choose the outdoor unit from the list or enter a custom size, then click to place it. Outside the house is fine; near a wall it turns to face away from it. R rotates it.",
            "**Radiator**: choose type, height and length, then click near a wall to put it flat against it, or anywhere to place it free.",
            "**Pipe**: click to start, click to add a bend, double-click or Enter to finish. Points snap to pipe ends, fittings, corners and walls. Choose the diameter and flow or return.",
            "**Fitting**: click on a pipe to fit it there.",
          ),
          p(
            "Pipe totals are listed by size, flow and return, for all storeys. Objects are saved with the survey. They are a layout drawing: they do not change the heat loss.",
          ),
        ],
      },
      {
        heading: "Fit warnings",
        blocks: [
          p(
            "Radiators and the outdoor unit are checked against the scan's walls, doors and windows: too long for the clear wall, hanging past the corner, blocking a door or opening, standing taller than a window sill above it, crossing a wall, an outdoor unit inside the house, or overlapping another object. Each problem is listed with its figures, and where a size would fit, it is suggested.",
          ),
          list(
            "The checks are advice. Nothing stops you saving; you may know something the scan does not.",
            "A radiator's height off the floor is assumed to be 150 mm until you set it.",
            "Where the floor level is not settled, the sill check is skipped and says why, rather than guessing.",
          ),
        ],
      },
      {
        heading: "The MCS design check",
        blocks: [
          p(
            "At the top of the Objects list. Every line says its state in words as well as colour: green meets the rule named, red does not, amber passes on the plan but needs checking on site, grey cannot be assessed yet and says what to enter. It needs the survey's heat loss.",
          ),
          list(
            "Rooms: the radiators in each room against its heat loss at the design flow temperature, with bigger sizes that fit. The worst room sets the design emitter temperature (MIS 3005-D §3.8.1(d)).",
            "Heat pump size: the design heat load the unit must meet (MIS 3005-D §3.4.1(e)), the output you enter for the placed unit, and the catalogue units whose datasheet output meets it.",
            "Outdoor unit: its wall, the manufacturer's clearances, and noise at the neighbours' windows (MCS 020 a). Place each neighbour's window on the plan, or add it by distance.",
          ),
        ],
      },
    ],
    related: ["exports-and-reports", "laser-calibration", "floors-and-stairs"],
  },

  // ------------------------------------------------------------------- 10 --
  {
    id: "exports-and-reports",
    title: "Exports and reports",
    summary:
      "Plan files (PDF, PNG, SVG, DXF), the room schedule, the stock list and the installer pack, plus the phone's own PDF report.",
    appliesTo: ["portal", "iphone"],
    topic: "portal",
    keywords: ["export", "download", "pdf", "png", "svg", "dxf", "cad", "autocad", "room schedule", "csv", "stock", "installer pack", "pick list", "bom", "bill of materials"],
    sections: [
      {
        heading: "The plan and the room schedule",
        blocks: [
          p("On the Survey page, in **Plan** view, click **Export**:"),
          list(
            "**Plan — PDF**: every floor, a sheet each, to scale.",
            "**Plan — PNG**: an image of the sheet, 200 dpi.",
            "**Plan — SVG**: vector, for editing or the web.",
            "**Plan — DXF (CAD)**: layered, in millimetres, for AutoCAD, Revit or DraftSight.",
            "**Room schedule — PDF**: areas and volumes per room, with where each came from.",
            "**Room schedule — CSV**: the same table, for a spreadsheet.",
          ),
          p(
            "Choose **Paper** (A3 or A4) and, when there is more than one storey, the **Floor** for the PNG, SVG and DXF. Each file is drawn from the plan as it stands when you ask, corrections included; nothing is stored.",
          ),
          note("caution", "Every export is drawn from the LiDAR scan. Check critical dimensions on site."),
        ],
      },
      {
        heading: "The stock list",
        blocks: [
          p(
            "On the Survey page, click **Stock & installer pack** at the top. Quantities are worked out from what is drawn on the plan (heat pumps, radiators, pipe runs) and from the design, and every line says how: the plan, the design, a catalogue rule, an assumption to check, and so on.",
          ),
          list(
            "Override any figure; the worked-out figure stays beside it, and **Use derived** puts it back.",
            "Leave a line out, or **Add a line for this job**.",
            "Set the allowances the plan cannot show: drop per radiator, pipe stock length, insulation coverage, and extra pipe for risers and the run to the outdoor unit.",
            "**Check before loading the van** lists anything that needs a look.",
            "The **Organisation stock catalogue** is your standing list: items with a rule are added to every job. Only an owner or admin can change it. No prices are kept.",
          ),
        ],
      },
      {
        heading: "The installer pack",
        blocks: [
          p(
            "Job card, design brief (MIS 3005-D 3.8.1), floor plans, radiator and pipe schedules, the pick list, commissioning checks and sign-off, with blanks for the team to fill in on site.",
          ),
          list(
            "**Preview installer pack** shows it on the page.",
            "**Download PDF** gives a draft, marked DRAFT on every page.",
            "**Issue installer pack**, then **Issue now — store a fixed copy**, keeps a fixed, fingerprinted copy, listed with **Download issued PDF**.",
          ),
        ],
      },
      {
        heading: "From the phone",
        blocks: [
          p(
            "**Projects** › the survey › **Share this survey** makes a PDF report with every photograph, plus the 3D model as a USDZ file when there is a scan, and hands them to WhatsApp, email, AirDrop or anything else on the phone.",
          ),
        ],
      },
    ],
    related: ["floor-plan", "sharing-a-survey", "floors-and-stairs"],
  },

  // ------------------------------------------------------------------- 11 --
  {
    id: "sharing-a-survey",
    title: "Sharing a survey",
    summary:
      "A private, read-only link for someone without a Scannix login: expiry, photographs off by default, withdrawing it. And the jobs map.",
    appliesTo: ["portal", "iphone"],
    topic: "portal",
    keywords: ["share", "link", "customer", "subcontractor", "expiry", "expire", "withdraw", "revoke", "photos", "privacy", "map", "jobs map"],
    sections: [
      {
        heading: "Make a link",
        blocks: [
          steps(
            "On the Survey page, click **Share**. Only an owner or admin sees the button.",
            "**Who is it for?** A label only you see, for example the customer's name or a subcontractor's quote.",
            "**Link stops working after** 7, 14, 30, 60 or 90 days. 30 is the default.",
            "Photographs are left out. To include some, tick **Include photographs I choose** and pick each one.",
            "Click **Make a share link**.",
          ),
          note(
            "caution",
            "Photographs show the inside of somebody's home and are printed with the GPS position they were taken at. Include only what the recipient needs.",
          ),
        ],
      },
      {
        heading: "Copy it now",
        blocks: [
          p(
            "The link is shown once. For security only a fingerprint of it is kept, so it cannot be shown again. If it is lost, withdraw it and make another.",
          ),
          p(
            "It opens the survey's floor plan and 3D model, and any photographs you chose, read-only. It is not listed or searchable anywhere.",
          ),
        ],
      },
      {
        heading: "Keeping track",
        blocks: [
          list(
            "Every link stays listed, **Live**, **Expired** or **Withdrawn**, with how many times it was opened.",
            "**Opens** shows each one: when, what was looked at (the page, the 3D model, a photograph) and from which address.",
            "**Withdraw**, then confirm, stops a link at once and for good.",
          ),
        ],
      },
      {
        heading: "From the phone",
        blocks: [
          p(
            "**Projects** › the survey › **Share a link to this survey** and **Open in the web portal** point a colleague with a portal login at the survey, for the detailed design. They need the portal address in the app's Settings, and work once the survey has finished uploading. They are not customer links.",
          ),
        ],
      },
      {
        heading: "The jobs map",
        blocks: [
          p(
            "**Jobs map** in the sidebar pins every job at its property, coloured by pipeline stage. It is private to your organisation; surveyors and installers see their own jobs.",
          ),
          list(
            "A pin goes at the property's postcode, or where the site photographs were taken when there is no usable postcode.",
            "A job that cannot be placed is still listed, with the reason.",
            "Filter by stage, surveyor and the date the job was created.",
            "If the map cannot load (offline, or map tiles blocked), the list below it still shows every job.",
          ),
        ],
      },
    ],
    related: ["exports-and-reports", "sending-a-survey", "3d-space"],
  },

  // ------------------------------------------------------------------- 12 --
  {
    id: "accuracy",
    title: "Accuracy: what LiDAR can and can't do",
    summary:
      "Sourced figures for how close a phone scan gets, where it goes wrong, and when to reach for a laser.",
    appliesTo: ["iphone", "portal"],
    topic: "background",
    keywords: ["lidar", "accuracy", "error", "tolerance", "centimetre", "cm", "mm", "laser", "research", "sources", "u-value"],
    sections: [
      {
        heading: "The short answer",
        blocks: [
          p(
            "A phone scan is good to a few centimetres, not millimetres. That is good enough for room areas and a heat loss. Check anything a design depends on to the last centimetre with a laser or tape.",
          ),
        ],
      },
      {
        heading: "The figures, and where they come from",
        blocks: [
          list(
            "About ±1 cm within 1 m of the phone, on small objects (Luetzenburg et al. 2021, Scientific Reports, iPhone 12 Pro).",
            "About ±2 cm at room scale, 1 to 3 m: 1.4 to 1.9 cm RMS against a terrestrial laser scanner (Spreafico et al. 2021, ISPRS).",
            "The sensor stops reading at about 4.9 m (Spreafico et al.). The tape measure refuses points beyond 4.5 m.",
            "Whole-room scanning apps: Canvas quotes \"within 1–2%\" (the vendor's own claim); an independent test of Polycam found about 2.5 cm at 1 m, getting worse with distance (GSA 2024).",
            "Apple's room scanner finds walls and windows 95% of the time and doors 90% (Apple Machine Learning Research), and names plate glass and mirrored walls as hard cases. Apple publishes no ± figure for its own Measure app.",
            "Auto-detected rectangles were about 3% out in a journalist's test (SlashGear): 3 cm on a 1 m radiator. That is why a detected width × height is never labelled better than ±2 cm.",
            "A handheld laser meter reads to about ±2 mm. That is the figure the laser calibration's check allows for.",
          ),
          note(
            "caution",
            "The ± bands the app shows are worked out from published studies of flat, matte, well-lit targets. They have not yet been checked against a tape on our own phones.",
          ),
        ],
      },
      {
        heading: "What it can't do",
        blocks: [
          list(
            "See glass and mirrors properly: a mirror reads as another room behind the wall.",
            "Read glossy or very dark surfaces reliably. The evidence here is mixed, so the app warns when the sensor's own confidence is low rather than guessing from colour.",
            "See what a wall is made of. U-values are assumptions until someone confirms them, and assumptions, not LiDAR, are the biggest error in a heat loss: BRE measured solid walls at about 1.6 W/m²K against an assumed 2.1.",
            "Stack floors exactly. Each room's sizes are sound; how storeys line up is approximate.",
            "Give a textured model to scale without LiDAR depth. Space Mode on a phone without LiDAR is **Not to scale**.",
          ),
        ],
      },
      {
        heading: "How Scannix labels its figures",
        blocks: [
          list(
            "Scan figures say **LiDAR scan**.",
            "Tape measurements carry their ± band and any warnings.",
            "A calibrated room says so, with who calibrated it and when.",
            "Nothing is shown as a laser figure unless a laser reading is behind it.",
          ),
        ],
      },
    ],
    related: ["tape-measure", "laser-calibration", "terraced-and-tight-rooms"],
  },
];

const BY_ID: ReadonlyMap<string, HelpGuide> = new Map(HELP_GUIDES.map((guide) => [guide.id, guide]));

/** The guide with this id, or undefined — the route carries whatever was typed. */
export function findGuide(id: string): HelpGuide | undefined {
  return BY_ID.get(id);
}
